import { Directive, OnDestroy, OnInit, signal } from '@angular/core';
import { Observable, Subject, Subscription, debounceTime, finalize } from 'rxjs';
import { atlasInvestorStatusOptionsWithAll } from './atlas-investor-filter-options';

type AtlasListPageEnvelope = {
  pageIndex: number;
  pageSize: number;
};

@Directive()
export abstract class AtlasDuplicateListPageBase<TPage extends AtlasListPageEnvelope>
  implements OnInit, OnDestroy {
  readonly investorStatusOptions = atlasInvestorStatusOptionsWithAll;
  readonly pageSizeOptions = [10, 25, 50, 100];
  readonly loading = signal(true);
  readonly error = signal<string | undefined>(undefined);
  readonly page = signal<TPage | undefined>(undefined);
  readonly pageIndex = signal(0);
  readonly pageSize = signal(25);
  readonly primaryFilter = signal('');
  readonly investorIdFilter = signal('');
  readonly investorFilter = signal('');
  readonly statusFilter = signal('');
  readonly consultantFilter = signal('');

  private readonly filterChanges = new Subject<void>();
  private loadSubscription?: Subscription;
  private filterSubscription?: Subscription;

  ngOnInit(): void {
    this.filterSubscription = this.filterChanges
      .pipe(debounceTime(400))
      .subscribe(() => {
        this.pageIndex.set(0);
        this.loadPage();
      });

    this.loadPage();
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.filterSubscription?.unsubscribe();
  }

  reload(): void {
    this.loadPage();
  }

  formatInlineValues(values: string[]): string {
    return values.join(' • ');
  }

  formatSingleOrInlineValues(values: string[]): string {
    return values.length > 0 ? this.formatInlineValues(values) : '-';
  }

  getActiveFiltersSummary(): string {
    const parts: string[] = [];
    const primaryFilterSummary = this.getPrimaryFilterSummary();

    if (primaryFilterSummary) {
      parts.push(primaryFilterSummary);
    }

    if (this.investorIdFilter().trim()) {
      parts.push(`Investisseur=${this.investorIdFilter().trim()}`);
    }

    if (this.investorFilter().trim()) {
      parts.push(`Texte investisseur~${this.investorFilter().trim()}`);
    }

    if (this.statusFilter().trim()) {
      parts.push(`Statut~${this.statusFilter().trim()}`);
    }

    if (this.consultantFilter().trim()) {
      parts.push(`Consultant~${this.consultantFilter().trim()}`);
    }

    return parts.join(' • ');
  }

  onPrimaryFilterChange(value: string): void {
    this.primaryFilter.set(value);
    this.queueFilterReload();
  }

  onInvestorIdFilterChange(value: string): void {
    this.investorIdFilter.set(value);
    this.queueFilterReload();
  }

  onStatusFilterChange(value: string): void {
    this.statusFilter.set(value);
    this.queueFilterReload();
  }

  onInvestorFilterChange(value: string): void {
    this.investorFilter.set(value);
    this.queueFilterReload();
  }

  onConsultantFilterChange(value: string): void {
    this.consultantFilter.set(value);
    this.queueFilterReload();
  }

  protected parseOptionalNumber(value: string): number | null {
    const trimmed = value.trim();

    if (!trimmed) {
      return null;
    }

    const parsedValue = Number(trimmed);

    return Number.isInteger(parsedValue) && parsedValue >= 0 ? parsedValue : null;
  }

  protected loadPage(): void {
    this.loading.set(true);
    this.error.set(undefined);
    this.loadSubscription?.unsubscribe();

    this.loadSubscription = this.loadPageRequest(
      this.pageIndex(),
      this.pageSize()
    )
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: page => {
          this.page.set(page);
          this.pageIndex.set(page.pageIndex);
          this.pageSize.set(page.pageSize);
        },
        error: error => {
          this.page.set(undefined);
          this.error.set(this.getLoadErrorMessage(error));
        }
      });
  }

  protected queueFilterReload(): void {
    this.filterChanges.next();
  }

  protected abstract loadPageRequest(pageIndex: number, pageSize: number): Observable<TPage>;
  protected abstract getLoadErrorMessage(error: unknown): string;
  protected abstract getPrimaryFilterSummary(): string | null;
}
